import { resolvePreset, type DateRange } from '@/components/shared/date-range-filter';

import {
  getCustomReportSource,
  type CustomReportSourceKey,
} from './custom-report-sources';
import { REPORT_FILTER_ANY } from './report-filters-bar';

export interface CustomReportFilterRow {
  field: string;
  operator: string;
  value: string;
}

export interface SeededCustomReportDraft {
  name: string;
  description: string | null;
  objectSource: CustomReportSourceKey;
  columns: string[];
  filters: CustomReportFilterRow[];
  sortOrder: { field: string; direction: 'asc' | 'desc' } | null;
  seededFromKey: string;
}

interface SeededCustomMapping {
  objectSource: CustomReportSourceKey;
  dateField?: string;
  /** Seeded filter key → custom field key. Omitted keys use the same name when the field exists. */
  filterFields?: Record<string, string>;
  implicitFilters?: CustomReportFilterRow[];
  sortOrder?: { field: string; direction: 'asc' | 'desc' };
}

const GIVING_CONTRIBUTION_FILTERS: SeededCustomMapping['filterFields'] = {
  fundId: 'fundId',
  contributionType: 'contributionType',
  method: 'method',
  memberId: 'memberId',
};

const CONTRIBUTIONS_BASE: SeededCustomMapping = {
  objectSource: 'contributions',
  dateField: 'contributionDate',
  sortOrder: { field: 'contributionDate', direction: 'desc' },
};

/** Ready-made reports that can be copied into the custom report builder. */
const SEEDED_CUSTOM_MAP: Record<string, SeededCustomMapping> = {
  income_by_fund: { ...CONTRIBUTIONS_BASE, filterFields: GIVING_CONTRIBUTION_FILTERS },
  giving_by_member: { ...CONTRIBUTIONS_BASE, filterFields: GIVING_CONTRIBUTION_FILTERS },
  giving_by_type: {
    ...CONTRIBUTIONS_BASE,
    filterFields: { fundId: 'fundId', method: 'method' },
  },
  tithe_summary: {
    ...CONTRIBUTIONS_BASE,
    filterFields: { fundId: 'fundId', method: 'method' },
    implicitFilters: [{ field: 'contributionType', operator: 'eq', value: 'tithe' }],
  },
  first_fruits_summary: {
    ...CONTRIBUTIONS_BASE,
    filterFields: { fundId: 'fundId', method: 'method' },
    implicitFilters: [{ field: 'contributionType', operator: 'eq', value: 'first_fruit' }],
  },
  top_givers: { ...CONTRIBUTIONS_BASE, filterFields: GIVING_CONTRIBUTION_FILTERS },
  giving_trend: { ...CONTRIBUTIONS_BASE, filterFields: GIVING_CONTRIBUTION_FILTERS },
  new_givers: {
    ...CONTRIBUTIONS_BASE,
    filterFields: { fundId: 'fundId', contributionType: 'contributionType' },
  },
  lapsed_givers: {
    ...CONTRIBUTIONS_BASE,
    filterFields: { fundId: 'fundId', contributionType: 'contributionType' },
  },
  giving_by_method: {
    ...CONTRIBUTIONS_BASE,
    filterFields: { fundId: 'fundId', contributionType: 'contributionType' },
  },
  giving_by_service_or_program: {
    ...CONTRIBUTIONS_BASE,
    filterFields: { fundId: 'fundId', contributionType: 'contributionType', method: 'method' },
  },
  pledge_fulfilment: {
    objectSource: 'pledges',
    dateField: 'startDate',
    filterFields: { fundId: 'fundId', status: 'status', memberId: 'memberId' },
    sortOrder: { field: 'startDate', direction: 'desc' },
  },
  project_progress: {
    objectSource: 'projects',
    filterFields: { status: 'status' },
    sortOrder: { field: 'deadline', direction: 'asc' },
  },

  expenses_by_category: {
    objectSource: 'expenses',
    dateField: 'expenseDate',
    filterFields: { categoryId: 'categoryId', fundId: 'fundId', method: 'method' },
    sortOrder: { field: 'expenseDate', direction: 'desc' },
  },
  expenses_by_fund: {
    objectSource: 'expenses',
    dateField: 'expenseDate',
    filterFields: { fundId: 'fundId', categoryId: 'categoryId' },
    sortOrder: { field: 'expenseDate', direction: 'desc' },
  },
  pending_expense_approvals: {
    objectSource: 'expenses',
    dateField: 'expenseDate',
    filterFields: { categoryId: 'categoryId', fundId: 'fundId', status: 'status' },
    sortOrder: { field: 'expenseDate', direction: 'desc' },
  },
  welfare_disbursements: {
    objectSource: 'welfare_requests',
    dateField: 'disbursedAt',
    filterFields: { memberId: 'memberId', requestType: 'requestType' },
    sortOrder: { field: 'disbursedAt', direction: 'desc' },
  },

  membership_roster: {
    objectSource: 'members',
    filterFields: {
      departmentId: 'departmentId',
      unitId: 'unitId',
      groupId: 'groupId',
      memberType: 'memberType',
    },
    sortOrder: { field: 'lastName', direction: 'asc' },
  },
  new_members: {
    objectSource: 'members',
    dateField: 'joinedAt',
    filterFields: { memberType: 'memberType', departmentId: 'departmentId', unitId: 'unitId' },
    sortOrder: { field: 'joinedAt', direction: 'desc' },
  },
  inactive_members: {
    objectSource: 'members',
    filterFields: { memberType: 'memberType', departmentId: 'departmentId', unitId: 'unitId' },
    sortOrder: { field: 'lastName', direction: 'asc' },
  },
  department_roster: {
    objectSource: 'members',
    filterFields: { departmentId: 'departmentId', unitId: 'unitId', memberType: 'memberType' },
    sortOrder: { field: 'lastName', direction: 'asc' },
  },
  baptism_records: {
    objectSource: 'members',
    dateField: 'joinedAt',
    filterFields: { departmentId: 'departmentId', unitId: 'unitId' },
    sortOrder: { field: 'joinedAt', direction: 'desc' },
  },

  service_attendance: {
    objectSource: 'attendance_sessions',
    dateField: 'occurrenceDate',
    filterFields: { status: 'status' },
    sortOrder: { field: 'occurrenceDate', direction: 'desc' },
  },
  first_time_visitors: {
    objectSource: 'check_ins',
    dateField: 'checkedInAt',
    sortOrder: { field: 'checkedInAt', direction: 'desc' },
  },
  member_check_ins: {
    objectSource: 'check_ins',
    dateField: 'checkedInAt',
    filterFields: { memberId: 'memberId', method: 'method' },
    sortOrder: { field: 'checkedInAt', direction: 'desc' },
  },

  group_roster: {
    objectSource: 'groups',
    implicitFilters: [{ field: 'type', operator: 'eq', value: 'manual' }],
    sortOrder: { field: 'name', direction: 'asc' },
  },
  smart_group_roster: {
    objectSource: 'groups',
    implicitFilters: [{ field: 'type', operator: 'eq', value: 'template' }],
    sortOrder: { field: 'name', direction: 'asc' },
  },
  group_health: {
    objectSource: 'groups',
    filterFields: { groupType: 'type', category: 'category' },
    sortOrder: { field: 'name', direction: 'asc' },
  },

  campaign_delivery_summary: {
    objectSource: 'campaigns',
    dateField: 'sentAt',
    filterFields: { status: 'status' },
    sortOrder: { field: 'sentAt', direction: 'desc' },
  },
  failed_deliveries: {
    objectSource: 'campaigns',
    dateField: 'sentAt',
    sortOrder: { field: 'sentAt', direction: 'desc' },
  },
};

const sourceFieldKeys = (objectSource: CustomReportSourceKey): Set<string> => {
  const source = getCustomReportSource(objectSource);
  return new Set((source?.fields ?? []).map((field) => field.key));
};

const defaultColumnsForSource = (objectSource: CustomReportSourceKey): string[] => {
  const source = getCustomReportSource(objectSource);
  return (source?.fields ?? []).filter((field) => !field.filterOnly).map((field) => field.key);
};

const resolveFilterField = (
  mapping: SeededCustomMapping,
  seededFilterKey: string,
  allowedFields: Set<string>,
): string | undefined => {
  const mapped = mapping.filterFields?.[seededFilterKey] ?? seededFilterKey;
  return allowedFields.has(mapped) ? mapped : undefined;
};

export const canSaveSeededAsCustom = (reportKey: string): boolean =>
  reportKey in SEEDED_CUSTOM_MAP;

export const isSeededReportAltered = (input: {
  supportsDateRange: boolean;
  dateRange: DateRange;
  timezone: string;
  reportFilters: Record<string, string>;
}): boolean => {
  const hasActiveFilters = Object.values(input.reportFilters).some(
    (value) => value !== REPORT_FILTER_ANY,
  );
  if (hasActiveFilters) return true;

  if (!input.supportsDateRange) return false;

  const defaultRange: DateRange = {
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', input.timezone),
  };

  if (input.dateRange.preset !== defaultRange.preset) return true;
  if (input.dateRange.from !== defaultRange.from || input.dateRange.to !== defaultRange.to) {
    return true;
  }

  return false;
};

export const buildSeededCustomReportDraft = (input: {
  reportKey: string;
  reportName: string;
  supportsDateRange: boolean;
  dateFrom: string | null;
  dateTo: string | null;
  activeFilters: Record<string, string>;
}): SeededCustomReportDraft | null => {
  const mapping = SEEDED_CUSTOM_MAP[input.reportKey];
  if (!mapping) return null;

  const allowedFields = sourceFieldKeys(mapping.objectSource);
  const filters: CustomReportFilterRow[] = [...(mapping.implicitFilters ?? [])];

  for (const [seededKey, value] of Object.entries(input.activeFilters)) {
    if (!value) continue;
    const field = resolveFilterField(mapping, seededKey, allowedFields);
    if (!field) continue;
    filters.push({ field, operator: 'eq', value });
  }

  if (mapping.dateField && input.supportsDateRange) {
    if (input.dateFrom) {
      filters.push({ field: mapping.dateField, operator: 'gte', value: input.dateFrom });
    }
    if (input.dateTo) {
      filters.push({ field: mapping.dateField, operator: 'lte', value: input.dateTo });
    }
  }

  return {
    name: input.reportName,
    description: null,
    objectSource: mapping.objectSource,
    columns: defaultColumnsForSource(mapping.objectSource),
    filters,
    sortOrder: mapping.sortOrder ?? null,
    seededFromKey: input.reportKey,
  };
};
